<div class="space-y-6 text-text-primary pb-8">

    @if (isLoading) {
    <div class="flex justify-center py-20 text-text-muted">
        <i class="fas fa-spinner fa-spin text-3xl"></i>
    </div>
    }

    @if (error && !isLoading) {
    <div
        class="flex items-center gap-3 px-6 py-4 bg-danger-soft border border-danger-border rounded-2xl text-danger text-sm font-bold">
        <i class="fas fa-exclamation-circle text-lg"></i><span>{{ error }}</span>
        <button (click)="load()" class="ml-auto underline hover:no-underline">{{ 'brokerDashboard.common.retry' |
            translate }}</button>
    </div>
    }

    @if (saveSuccess) {
    <div
        class="flex items-center gap-3 px-6 py-4 bg-success-soft border border-success-border rounded-2xl text-success text-sm font-bold">
        <i class="fas fa-check-circle text-lg"></i><span>{{ saveSuccess }}</span>
    </div>
    }

    @if (profile && !isLoading) {

    <!-- Profile Header Card -->
    <div
        class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 md:p-8 relative overflow-hidden group hover:border-primary-border transition-colors">
        <div
            class="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        </div>
        <div
            class="absolute right-0 top-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors">
        </div>

        <div class="flex flex-col sm:flex-row items-center sm:items-start gap-6 relative z-10">
            <!-- Logo / Avatar -->
            <div class="relative flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
                @if (profile.profileImage || profile.logo) {
                <img [src]="profile.profileImage || profile.logo" [alt]="profile.firstName"
                    class="w-24 h-24 rounded-2xl object-cover shadow-elevated border-2 border-border" />
                } @else {
                <div
                    class="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center text-white text-3xl font-bold shadow-elevated border-2 border-border">
                    {{ profile.firstName[0] }}{{ profile.lastName[0] }}
                </div>
                }
                @if (profile.isTrusted) {
                <span
                    class="absolute -top-2 -right-2 w-7 h-7 bg-info rounded-full flex items-center justify-center border-2 border-surface-elevated shadow-sm">
                    <i class="fas fa-check text-white text-xs"></i>
                </span>
                }
            </div>

            <!-- Name + meta -->
            <div class="flex-1 text-center sm:text-left">
                <div class="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                    <h2 class="text-2xl font-bold text-text-primary tracking-tight">{{ profile.firstName }} {{
                        profile.lastName }}</h2>
                    <div class="flex items-center justify-center sm:justify-start gap-2">
                        <span
                            class="px-3 py-1 rounded-md text-[10px] font-bold bg-primary-soft text-primary border border-primary-border uppercase tracking-wider">
                            {{ profile.brokerType }}
                        </span>
                        <span class="px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider"
                            [ngClass]="profile.ShowProfile === 'public' ? 'bg-success-soft text-success border border-success-border' : 'bg-warning-soft text-warning border border-warning-border'">
                            {{ profile.ShowProfile }}
                        </span>
                    </div>
                </div>
                <p
                    class="text-sm font-medium text-text-secondary mb-3 flex items-center justify-center sm:justify-start gap-2">
                    <i class="fas fa-envelope text-text-muted"></i> {{ profile.email }}
                </p>
                @if (profile.aboutMe) {
                <p class="text-sm font-medium text-text-secondary mb-4 text-center sm:text-left max-w-2xl mx-auto sm:mx-0 whitespace-pre-line leading-relaxed">
                    {{ profile.aboutMe }}
                </p>
                }

                <div
                    class="flex items-center justify-center sm:justify-start gap-1 bg-surface border border-border-subtle px-3 py-1.5 rounded-lg w-fit mx-auto sm:mx-0">
                    <div class="flex gap-0.5">
                        @for (s of stars(profile.averageRating); track s) {
                        <i class="fas fa-star text-[10px]"
                            [ngClass]="s <= profile.averageRating ? 'text-warning' : 'text-text-muted'"></i>
                        }
                    </div>
                    <span class="text-xs font-bold text-text-secondary ml-2 border-l border-border-subtle pl-2">
                        {{ profile.averageRating }}
                        <span class="text-text-muted font-medium">({{ profile.totalReviews }} {{
                            'brokerDashboard.profile.reviews' | translate }})</span>
                    </span>
                </div>
            </div>

            <!-- Edit button -->
            <button (click)="openEdit()"
                class="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-surface border border-border hover:border-primary hover:text-primary text-text-primary text-xs font-bold uppercase tracking-wider rounded-xl transition-colors shadow-subtle group/btn">
                <i class="fas fa-pen-to-square group-hover/btn:scale-110 transition-transform"></i>
                {{ 'brokerDashboard.profile.editProfile' | translate }}
            </button>
        </div>
    </div>

    <!-- Info Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

        <!-- Contact Info -->
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-info-soft border border-info-border flex items-center justify-center text-info">
                    <i class="fas fa-address-card"></i>
                </div>
                {{ 'brokerDashboard.profile.contactInfo' | translate }}
            </h3>
            <div class="space-y-4">
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-phone text-xs"></i>
                    </div>
                    <span class="font-bold text-text-primary">{{ profile.phoneNumber }}</span>
                </div>
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-envelope text-xs"></i>
                    </div>
                    <span class="font-bold text-text-primary">{{ profile.email }}</span>
                </div>
                @if (profile.location) {
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-map-marker-alt text-xs"></i>
                    </div>
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">Location</span>
                        <span class="font-bold text-text-primary">{{ profile.location }}</span>
                    </div>
                </div>
                }
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-calendar-alt text-xs"></i>
                    </div>
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">{{
                            'brokerDashboard.profile.memberSince' | translate }}</span>
                        <span class="font-bold text-text-primary">{{ formatDate(profile.createdAt) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Business Info -->
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-warning-soft border border-warning-border flex items-center justify-center text-warning">
                    <i class="fas fa-briefcase"></i>
                </div>
                {{ 'brokerDashboard.profile.businessInfo' | translate }}
            </h3>
            <div class="space-y-4">
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-id-badge text-xs"></i>
                    </div>
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">{{
                            'brokerDashboard.profile.license' | translate }}</span>
                        <span class="font-bold text-text-primary">{{ profile.licenseNumber }}</span>
                    </div>
                </div>
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-layer-group text-xs"></i>
                    </div>
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">{{
                            'brokerDashboard.profile.specialization' | translate }}</span>
                        <span class="font-bold text-text-primary capitalize">{{ profile.specializations }}</span>
                    </div>
                </div>
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-clock text-xs"></i>
                    </div>
                    <div>
                        <span
                            class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">Experience</span>
                        <span class="font-bold text-text-primary">{{ profile.yearsOfExperience }} {{
                            'brokerDashboard.profile.yearsOfExperience' | translate }}</span>
                    </div>
                </div>
                @if (profile.companyName) {
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-building text-xs"></i>
                    </div>
                    <div>
                        <span
                            class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">Company</span>
                        <span class="font-bold text-text-primary">{{ profile.companyName }}</span>
                    </div>
                </div>
                }
                @if (profile.commercialRegisterNumber) {
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-file-invoice text-xs"></i>
                    </div>
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">CR Number</span>
                        <span class="font-bold text-text-primary">{{ profile.commercialRegisterNumber }}</span>
                    </div>
                </div>
                }
                @if (profile.website) {
                <div class="flex items-center gap-4 text-sm bg-surface p-3 rounded-xl border border-border">
                    <div
                        class="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center flex-shrink-0 text-text-muted">
                        <i class="fas fa-globe text-xs"></i>
                    </div>
                    <div class="overflow-hidden">
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-0.5">Website</span>
                        <a [href]="profile.website" target="_blank" class="font-bold text-primary hover:underline block truncate">{{ profile.website }}</a>
                    </div>
                </div>
                }
                @if (profile.aboutCompany_ar || profile.aboutCompany_en) {
                <div class="flex flex-col gap-3 text-sm bg-surface p-4 rounded-xl border border-border">
                    @if (profile.aboutCompany_ar) {
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-1">About Company (AR)</span>
                        <p class="font-medium text-text-primary leading-relaxed whitespace-pre-line">{{ profile.aboutCompany_ar }}</p>
                    </div>
                    }
                    @if (profile.aboutCompany_en) {
                    <div>
                        <span class="text-[10px] font-bold text-text-secondary uppercase tracking-wider block mb-1">About Company (EN)</span>
                        <p class="font-medium text-text-primary leading-relaxed whitespace-pre-line">{{ profile.aboutCompany_en }}</p>
                    </div>
                    }
                </div>
                }
            </div>
        </div>

    </div>

    <!-- Service Areas & Languages -->
    @if ((profile.serviceAreas && profile.serviceAreas.length) || (profile.languages && profile.languages.length)) {
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        @if (profile.serviceAreas && profile.serviceAreas.length) {
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-primary-soft border border-primary-border flex items-center justify-center text-primary">
                    <i class="fas fa-map-marker-alt"></i>
                </div>
                {{ 'brokerDashboard.profile.serviceAreas' | translate }}
            </h3>
            <div class="flex flex-wrap gap-2">
                @for (area of profile.serviceAreas; track area) {
                <span
                    class="px-3 py-1.5 bg-surface border border-border text-text-primary text-xs font-bold rounded-lg">{{
                    area }}</span>
                }
            </div>
        </div>
        }

        @if (profile.languages && profile.languages.length) {
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-info-soft border border-info-border flex items-center justify-center text-info">
                    <i class="fas fa-language"></i>
                </div>
                {{ 'brokerDashboard.profile.languages' | translate }}
            </h3>
            <div class="flex flex-wrap gap-2">
                @for (lang of profile.languages; track lang) {
                <span
                    class="px-3 py-1.5 bg-surface border border-border text-text-primary text-xs font-bold rounded-lg capitalize">{{
                    lang }}</span>
                }
            </div>
        </div>
        }
    </div>
    }

    <!-- Bottom Section -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        <!-- Social Links -->
        @if (profile.socialLinks && (profile.socialLinks.linkedin || profile.socialLinks.facebook ||
        profile.socialLinks.instagram || profile.socialLinks.twitter)) {
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-surface border border-border-strong flex items-center justify-center text-text-primary">
                    <i class="fas fa-share-nodes"></i>
                </div>
                {{ 'brokerDashboard.profile.socialLinks' | translate }}
            </h3>
            <div class="flex flex-col gap-3">
                @if (profile.socialLinks.linkedin) {
                <a [href]="profile.socialLinks.linkedin" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3 bg-surface border border-border rounded-xl text-sm font-bold text-text-secondary hover:border-[#0077b5] hover:text-[#0077b5] transition-colors group">
                    <i class="fab fa-linkedin text-lg group-hover:scale-110 transition-transform"></i> LinkedIn
                </a>
                }
                @if (profile.socialLinks.facebook) {
                <a [href]="profile.socialLinks.facebook" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3 bg-surface border border-border rounded-xl text-sm font-bold text-text-secondary hover:border-[#1877f2] hover:text-[#1877f2] transition-colors group">
                    <i class="fab fa-facebook text-lg group-hover:scale-110 transition-transform"></i> Facebook
                </a>
                }
                @if (profile.socialLinks.instagram) {
                <a [href]="profile.socialLinks.instagram" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3 bg-surface border border-border rounded-xl text-sm font-bold text-text-secondary hover:border-[#e1306c] hover:text-[#e1306c] transition-colors group">
                    <i class="fab fa-instagram text-lg group-hover:scale-110 transition-transform"></i> Instagram
                </a>
                }
                @if (profile.socialLinks.twitter) {
                <a [href]="profile.socialLinks.twitter" target="_blank" rel="noopener noreferrer"
                    class="flex items-center gap-3 p-3 bg-surface border border-border rounded-xl text-sm font-bold text-text-secondary hover:border-text-primary hover:text-text-primary transition-colors group">
                    <i class="fab fa-x-twitter text-lg group-hover:scale-110 transition-transform"></i> X / Twitter
                </a>
                }
            </div>
        </div>
        }

        <!-- Notification Settings Readonly -->
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-success-soft border border-success-border flex items-center justify-center text-success">
                    <i class="fas fa-bell"></i>
                </div>
                {{ 'brokerDashboard.profile.notifications' | translate }}
            </h3>
            <div class="flex flex-col gap-4 text-sm">
                <div class="flex items-center justify-between p-3 bg-surface border border-border rounded-xl">
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs"
                            [ngClass]="profile.notificationSettings.email ? 'bg-success-soft text-success' : 'bg-surface-hover text-text-muted'">
                            <i class="fas fa-envelope"></i>
                        </div>
                        <span class="font-bold text-text-primary">{{ 'brokerDashboard.profile.emailNotifications' |
                            translate }}</span>
                    </div>
                    <span class="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        [ngClass]="profile.notificationSettings.email ? 'bg-success/10 text-success' : 'bg-surface-hover text-text-secondary'">
                        {{ profile.notificationSettings.email ? ('brokerDashboard.profile.enabled' | translate) :
                        ('brokerDashboard.profile.disabled' | translate) }}
                    </span>
                </div>
                <div class="flex items-center justify-between p-3 bg-surface border border-border rounded-xl">
                    <div class="flex items-center gap-3">
                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-xs"
                            [ngClass]="profile.notificationSettings.app ? 'bg-success-soft text-success' : 'bg-surface-hover text-text-muted'">
                            <i class="fas fa-mobile-alt"></i>
                        </div>
                        <span class="font-bold text-text-primary">{{ 'brokerDashboard.profile.appNotifications' |
                            translate }}</span>
                    </div>
                    <span class="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        [ngClass]="profile.notificationSettings.app ? 'bg-success/10 text-success' : 'bg-surface-hover text-text-secondary'">
                        {{ profile.notificationSettings.app ? ('brokerDashboard.profile.enabled' | translate) :
                        ('brokerDashboard.profile.disabled' | translate) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- License Document -->
        @if (profile.licenseDocument) {
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-warning-soft border border-warning-border flex items-center justify-center text-warning">
                    <i class="fas fa-file-contract"></i>
                </div>
                {{ 'brokerDashboard.profile.licenseDocument' | translate }}
            </h3>

            <div
                class="p-6 bg-surface border border-border rounded-xl flex flex-col items-center justify-center text-center gap-4 h-[calc(100%-4rem)]">
                <div class="w-16 h-16 bg-surface-hover rounded-full flex items-center justify-center text-text-muted">
                    <i class="fas fa-file-pdf text-2xl"></i>
                </div>
                <a [href]="profile.licenseDocument" target="_blank"
                    class="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-surface-hover border border-border-strong rounded-xl text-xs font-bold text-text-primary hover:border-primary hover:text-primary transition-colors">
                    <i class="fas fa-external-link-alt"></i> {{ 'brokerDashboard.profile.viewDocument' | translate }}
                </a>
            </div>
        </div>
        }

        <!-- Commercial Register Document -->
        @if (profile.commercialRegisterDocument) {
        <div
            class="bg-surface-elevated rounded-2xl border border-border shadow-subtle p-6 hover:border-border-strong transition-colors">
            <h3
                class="text-xs font-bold text-text-secondary uppercase tracking-wider mb-5 flex items-center gap-3 border-b border-border-subtle pb-3">
                <div
                    class="w-8 h-8 rounded-lg bg-info-soft border border-info-border flex items-center justify-center text-info">
                    <i class="fas fa-file-invoice"></i>
                </div>
                Commercial Register Document
            </h3>

            <div
                class="p-6 bg-surface border border-border rounded-xl flex flex-col items-center justify-center text-center gap-4 h-[calc(100%-4rem)]">
                <div class="w-16 h-16 bg-surface-hover rounded-full flex items-center justify-center text-text-muted">
                    <i class="fas fa-file-pdf text-2xl"></i>
                </div>
                <a [href]="profile.commercialRegisterDocument" target="_blank"
                    class="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-surface-hover border border-border-strong rounded-xl text-xs font-bold text-text-primary hover:border-primary hover:text-primary transition-colors">
                    <i class="fas fa-external-link-alt"></i> {{ 'brokerDashboard.profile.viewDocument' | translate }}
                </a>
            </div>
        </div>
        }

    </div>

    }
</div>

<!-- ── Edit Profile Slide Panel ────────────────────────────────────────── -->
@if (showEditPanel) {
<!-- Backdrop -->
<div class="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity" (click)="closeEdit()"></div>

<!-- Panel -->
<div
    class="fixed inset-y-0 end-0 z-50 w-full max-w-lg bg-surface-elevated shadow-2xl flex flex-col overflow-hidden border-l border-border transition-transform">

    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-5 border-b border-border">
        <h2 class="text-lg font-bold text-text-primary flex items-center gap-3">
            <div
                class="w-8 h-8 bg-primary-soft border border-primary-border rounded-lg flex items-center justify-center text-primary">
                <i class="fas fa-pen-to-square"></i>
            </div>
            {{ 'brokerDashboard.profile.editProfile' | translate }}
        </h2>
        <button (click)="closeEdit()"
            class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-hover text-text-secondary hover:text-danger transition-colors">
            <i class="fas fa-xmark text-lg"></i>
        </button>
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto px-6 py-6 space-y-8 custom-scrollbar">

        <!-- Save error -->
        @if (saveError) {
        <div
            class="flex items-center gap-3 px-4 py-3 bg-danger-soft border border-danger-border rounded-xl text-danger text-sm font-bold">
            <i class="fas fa-exclamation-circle"></i><span>{{ saveError }}</span>
        </div>
        }

        <!-- Logo upload -->
        <div>
            <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-3">
                {{ 'brokerDashboard.profile.logo' | translate }}
            </label>
            <div class="flex items-center gap-5 p-4 bg-surface border border-border rounded-2xl">
                <div
                    class="w-16 h-16 rounded-xl overflow-hidden bg-surface-hover border border-border-subtle flex-shrink-0">
                    @if (logoPreview) {
                    <img [src]="logoPreview" alt="Logo preview" class="w-full h-full object-cover" />
                    } @else if (profile?.profileImage || profile?.logo) {
                    <img [src]="profile!.profileImage || profile!.logo" [alt]="profile!.firstName" class="w-full h-full object-cover" />
                    } @else {
                    <div
                        class="w-full h-full bg-gradient-to-br from-primary to-primary-hover flex items-center justify-center text-white font-bold text-xl">
                        {{ profile?.firstName?.[0] }}{{ profile?.lastName?.[0] }}
                    </div>
                    }
                </div>
                <label
                    class="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-surface-hover border border-border-strong rounded-xl text-xs font-bold text-text-primary hover:border-primary hover:text-primary transition-colors">
                    <i class="fas fa-upload"></i>
                    {{ 'brokerDashboard.profile.changeLogo' | translate }}
                    <input type="file" class="hidden" accept="image/jpeg,image/png,image/webp"
                        (change)="onLogoChange($event)" />
                </label>
            </div>
        </div>

        <!-- Personal Info -->
        <div>
            <p
                class="text-[10px] font-bold text-primary uppercase tracking-wider mb-4 pb-2 border-b border-border-subtle">
                {{ 'brokerDashboard.profile.personalInfo' | translate }}
            </p>
            <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.firstName' | translate }}</label>
                    <input type="text" [(ngModel)]="editForm.firstName"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
                <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.lastName' | translate }}</label>
                    <input type="text" [(ngModel)]="editForm.lastName"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
            </div>
            <div class="mt-4 space-y-1.5">
                <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">Email</label>
                <input type="email" [(ngModel)]="editForm.email"
                    class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
            </div>
            <div class="mt-4 space-y-1.5">
                <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                    'brokerDashboard.profile.phone' | translate }}</label>
                <input type="tel" [(ngModel)]="editForm.phoneNumber"
                    class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
            </div>
            <div class="mt-4 space-y-1.5">
                <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">About Me</label>
                <textarea [(ngModel)]="editForm.aboutMe" rows="3"
                    class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"></textarea>
            </div>
            <div class="mt-4 space-y-1.5">
                <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">Location</label>
                <input type="text" [(ngModel)]="editForm.location"
                    class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
            </div>
        </div>

        <!-- Business Info -->
        <div>
            <p
                class="text-[10px] font-bold text-warning uppercase tracking-wider mb-4 pb-2 border-b border-border-subtle">
                {{ 'brokerDashboard.profile.businessInfo' | translate }}
            </p>
            <div class="space-y-4">
                @if (profile?.brokerType === 'company') {
                <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-1.5">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                            'brokerDashboard.profile.companyName' | translate }}</label>
                        <input type="text" [(ngModel)]="editForm.companyName"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    </div>
                    <div class="space-y-1.5">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">CR
                            Number</label>
                        <input type="text" [(ngModel)]="editForm.commercialRegisterNumber"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    </div>
                    <div class="space-y-1.5">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">Website</label>
                        <input type="url" [(ngModel)]="editForm.website"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    </div>
                    <div class="col-span-2 space-y-1.5 mt-2">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">About Company (AR)</label>
                        <textarea [(ngModel)]="editForm.aboutCompany_ar" rows="3"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"></textarea>
                    </div>
                    <div class="col-span-2 space-y-1.5 mt-2">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">About Company (EN)</label>
                        <textarea [(ngModel)]="editForm.aboutCompany_en" rows="3"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"></textarea>
                    </div>
                    <div class="col-span-2 space-y-1.5 mt-2">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">Commercial Register Document</label>
                        <div class="flex items-center gap-3">
                            <label class="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-surface border border-border rounded-xl text-xs font-bold text-text-primary hover:border-info hover:text-info transition-colors">
                                <i class="fas fa-file-arrow-up"></i>
                                @if (commercialRegisterFileName) {
                                <span class="truncate max-w-[200px]">{{ commercialRegisterFileName }}</span>
                                } @else {
                                Upload Document
                                }
                                <input type="file" class="hidden" accept=".pdf,image/jpeg,image/png" (change)="onCommercialRegisterChange($event)" />
                            </label>
                            @if (profile?.commercialRegisterDocument && !commercialRegisterFileName) {
                            <a [href]="profile!.commercialRegisterDocument" target="_blank"
                                class="text-xs font-bold text-info hover:underline">
                                {{ 'brokerDashboard.profile.viewDocument' | translate }}
                            </a>
                            }
                        </div>
                    </div>
                </div>
                }
                <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.license' | translate }}</label>
                    <input type="text" [(ngModel)]="editForm.licenseNumber"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
                <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-1.5">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                            'brokerDashboard.profile.specialization' | translate }}</label>
                        <select [(ngModel)]="editForm.specializations"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all">
                            <option value="residential">{{ 'brokerDashboard.settings.specResidential' | translate }}
                            </option>
                            <option value="commercial">{{ 'brokerDashboard.settings.specCommercial' | translate }}
                            </option>
                            <option value="both">{{ 'brokerDashboard.settings.specBoth' | translate }}</option>
                        </select>
                    </div>
                    <div class="space-y-1.5">
                        <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                            'brokerDashboard.profile.yearsOfExperience' | translate }}</label>
                        <input type="number" [(ngModel)]="editForm.yearsOfExperience" min="0"
                            class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    </div>
                </div>
                <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.serviceAreasLabel' | translate }}</label>
                    <input type="text" [(ngModel)]="editForm.serviceAreas" placeholder="Cairo, Giza"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    <p class="text-[10px] font-bold text-text-muted mt-1">{{ 'brokerDashboard.profile.commaSeparated' |
                        translate }}</p>
                </div>
                <div class="space-y-1.5">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.languagesLabel' | translate }}</label>
                    <input type="text" [(ngModel)]="editForm.languages" placeholder="Arabic, English"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                    <p class="text-[10px] font-bold text-text-muted mt-1">{{ 'brokerDashboard.profile.commaSeparated' |
                        translate }}</p>
                </div>
            </div>
        </div>

        <!-- License Document -->
        <div>
            <label
                class="block text-[10px] font-bold text-info uppercase tracking-wider mb-3 pb-2 border-b border-border-subtle">
                {{ 'brokerDashboard.profile.licenseDocument' | translate }}
            </label>
            <div class="flex items-center gap-3">
                <label
                    class="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 bg-surface border border-border rounded-xl text-xs font-bold text-text-primary hover:border-info hover:text-info transition-colors">
                    <i class="fas fa-file-arrow-up"></i>
                    @if (licenseFileName) {
                    <span class="truncate max-w-[200px]">{{ licenseFileName }}</span>
                    } @else {
                    {{ 'brokerDashboard.profile.replaceDocument' | translate }}
                    }
                    <input type="file" class="hidden" accept=".pdf,image/jpeg,image/png"
                        (change)="onLicenseChange($event)" />
                </label>
                @if (profile?.licenseDocument && !licenseFileName) {
                <a [href]="profile!.licenseDocument" target="_blank"
                    class="text-xs font-bold text-info hover:underline">
                    {{ 'brokerDashboard.profile.viewDocument' | translate }}
                </a>
                }
            </div>
        </div>

        <!-- Social Links -->
        <div>
            <p
                class="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-4 pb-2 border-b border-border-subtle">
                {{ 'brokerDashboard.profile.socialLinks' | translate }}
            </p>
            <div class="space-y-4">
                <div class="flex items-center gap-3">
                    <div class="w-8 flex justify-center"><i
                            class="fab fa-linkedin text-xl text-text-muted hover:text-[#0077b5] transition-colors"></i>
                    </div>
                    <input type="url" [(ngModel)]="editForm.socialLinkedin" placeholder="https://linkedin.com/in/..."
                        class="flex-1 px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
                <div class="flex items-center gap-3">
                    <div class="w-8 flex justify-center"><i
                            class="fab fa-facebook text-xl text-text-muted hover:text-[#1877f2] transition-colors"></i>
                    </div>
                    <input type="url" [(ngModel)]="editForm.socialFacebook" placeholder="https://facebook.com/..."
                        class="flex-1 px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
                <div class="flex items-center gap-3">
                    <div class="w-8 flex justify-center"><i
                            class="fab fa-instagram text-xl text-text-muted hover:text-[#e1306c] transition-colors"></i>
                    </div>
                    <input type="url" [(ngModel)]="editForm.socialInstagram" placeholder="https://instagram.com/..."
                        class="flex-1 px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
                <div class="flex items-center gap-3">
                    <div class="w-8 flex justify-center"><i
                            class="fab fa-x-twitter text-xl text-text-muted hover:text-text-primary transition-colors"></i>
                    </div>
                    <input type="url" [(ngModel)]="editForm.socialTwitter" placeholder="https://x.com/..."
                        class="flex-1 px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all" />
                </div>
            </div>
        </div>

        <!-- Privacy & Notifications -->
        <div>
            <p
                class="text-[10px] font-bold text-success uppercase tracking-wider mb-4 pb-2 border-b border-border-subtle">
                {{ 'brokerDashboard.profile.privacyNotifications' | translate }}
            </p>
            <div class="space-y-5">
                <div class="space-y-2">
                    <label class="block text-[10px] font-bold text-text-secondary uppercase tracking-wider">{{
                        'brokerDashboard.profile.profileVisibility' | translate }}</label>
                    <select [(ngModel)]="editForm.ShowProfile"
                        class="w-full px-4 py-2.5 bg-surface-input border border-border rounded-xl text-sm font-medium text-text-primary focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all">
                        <option value="public">{{ 'brokerDashboard.settings.visibilityPublic' | translate }}</option>
                        <option value="registered">{{ 'brokerDashboard.settings.visibilityRegistered' | translate }}
                        </option>
                        <option value="private">{{ 'brokerDashboard.settings.visibilityPrivate' | translate }}</option>
                    </select>
                </div>
                <div class="flex items-center justify-between p-3 bg-surface rounded-xl border border-border">
                    <span class="text-sm font-bold text-text-primary flex items-center gap-3">
                        <i class="fas fa-envelope text-text-muted"></i>
                        {{ 'brokerDashboard.profile.emailNotifications' | translate }}
                    </span>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" [(ngModel)]="editForm.notificationEmail" class="sr-only peer" />
                        <div
                            class="w-10 h-5 bg-surface-hover border border-border-strong rounded-full peer peer-checked:bg-success peer-checked:border-success after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5">
                        </div>
                    </label>
                </div>
                <div class="flex items-center justify-between p-3 bg-surface rounded-xl border border-border">
                    <span class="text-sm font-bold text-text-primary flex items-center gap-3">
                        <i class="fas fa-mobile-alt text-text-muted"></i>
                        {{ 'brokerDashboard.profile.appNotifications' | translate }}
                    </span>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" [(ngModel)]="editForm.notificationApp" class="sr-only peer" />
                        <div
                            class="w-10 h-5 bg-surface-hover border border-border-strong rounded-full peer peer-checked:bg-success peer-checked:border-success after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5">
                        </div>
                    </label>
                </div>
            </div>
        </div>

    </div>

    <!-- Footer actions -->
    <div class="flex items-center justify-end gap-3 px-6 py-5 border-t border-border bg-surface-elevated z-10">
        <button (click)="closeEdit()" [disabled]="isSaving"
            class="px-5 py-2.5 text-xs font-bold text-text-secondary border border-border bg-surface rounded-xl hover:bg-surface-hover hover:text-text-primary transition-colors disabled:opacity-50 uppercase tracking-wider">
            {{ 'brokerDashboard.common.cancel' | translate }}
        </button>
        <button (click)="saveProfile()" [disabled]="isSaving"
            class="px-6 py-2.5 text-xs font-bold text-white bg-primary shadow-primary-glow rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-60 flex items-center gap-2 uppercase tracking-wider">
            @if (isSaving) {
            <i class="fas fa-spinner fa-spin text-sm"></i>
            } @else {
            <i class="fas fa-save text-sm"></i>
            }
            {{ isSaving ? ('brokerDashboard.common.saving' | translate) : ('brokerDashboard.common.saveChanges' |
            translate) }}
        </button>
    </div>
</div>
}